import React from "react";
import {
  UseCaseConfig,
  FormConfigField,
  ChecklistItem,
  COMPARISON_MODE,
  MEDIA_FIELD_TYPES,
} from "../shared/types";
import * as S from "../shared/styles";

// ----------------------------  FormPreview  ----------------------------

interface FormPreviewProps {
  config: UseCaseConfig;
  showFields?: boolean;
  showChecklist?: boolean;
}

const FormPreview: React.FC<FormPreviewProps> = ({
  config,
  showFields = true,
  showChecklist = true,
}) => {
  const fields = showFields
    ? [...config.fields].sort((a, b) => a.display_order - b.display_order)
    : [];
  const checklist = showChecklist
    ? [...config.checklist_items].sort((a, b) => a.display_order - b.display_order)
    : [];

  if (!fields.length && !checklist.length) {
    return (
      <p style={{ fontSize: "0.875rem", color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>
        {showFields ? "Add fields to see a preview." : "Add checklist items to see a preview."}
      </p>
    );
  }

  const isOverride = config.comparison_mode === COMPARISON_MODE.OVERRIDE;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Mode indicator (only when showing fields) */}
      {showFields && fields.length > 0 && (
        <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
          Preview mode: <strong>{isOverride ? "Override" : "Simple"}</strong>
        </div>
      )}

      {/* Field previews */}
      {fields.map((f) => (
        <FieldPreviewCard key={f.id} field={f} isOverride={isOverride} />
      ))}

      {/* Checklist preview */}
      {checklist.length > 0 && (
        <div style={previewCard}>
          <div style={previewFieldLabel}>Checklist</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {checklist.map((item) => (
              <div key={item.id} style={checklistPreviewRow}>
                <span style={{ fontSize: "0.875rem", color: "#374151" }}>
                  {item.label || "(untitled item)"}
                </span>
                <span style={{ display: "flex", gap: "4px" }}>
                  {["Pass", "Fail", "N/A"].map((r) => (
                    <span key={r} style={checklistPill}>
                      {r}
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------  Field Preview Card  ----------------------------

const FieldPreviewCard: React.FC<{ field: FormConfigField; isOverride: boolean }> = ({
  field,
  isOverride,
}) => {
  const isMedia = MEDIA_FIELD_TYPES.indexOf(field.type) !== -1;

  return (
    <div style={previewCard}>
      <div style={previewFieldLabel}>
        {field.label || "(untitled field)"}
        {field.mandatory && <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>}
      </div>
      {isMedia ? (
        <div style={mediaPlaceholder}>{field.type.replace(/_/g, " ")}</div>
      ) : isOverride ? (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <div style={colHead}>AI value</div>
            <div style={{ ...colHead, color: "#f59e0b" }}>Human value</div>
            <div style={colHead}>Decision</div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <div style={valueBoxAi}>value</div>
            <div style={valueBoxHuman}>value</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {["AI Correct", "Human Correct", "Both Wrong"].map((d) => (
                <span key={d} style={decisionPill}>
                  {d}
                </span>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={valueBoxAi}>value</div>
          <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
            {["Correct", "Incorrect"].map((d) => (
              <span key={d} style={decisionPill}>
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
      {field.help_text && (
        <div style={{ fontSize: "0.7rem", color: "#9ca3af", marginTop: "4px" }}>
          {field.help_text}
        </div>
      )}
    </div>
  );
};

// ----------------------------  Preview Styles  ----------------------------

const previewCard: React.CSSProperties = {
  background: "white",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  padding: "12px",
};

const previewFieldLabel: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const colHead: React.CSSProperties = {
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#0f7b8a",
};

const valueBoxAi: React.CSSProperties = {
  background: "#e6f7f7",
  borderRadius: "4px",
  padding: "6px 8px",
  fontSize: "0.75rem",
  color: "#6b7280",
};

const valueBoxHuman: React.CSSProperties = {
  background: "#fef3c7",
  borderRadius: "4px",
  padding: "6px 8px",
  fontSize: "0.75rem",
  color: "#6b7280",
};

const decisionPill: React.CSSProperties = {
  fontSize: "0.65rem",
  padding: "2px 8px",
  border: "1px solid #e5e7eb",
  borderRadius: "4px",
  color: "#9ca3af",
};

const mediaPlaceholder: React.CSSProperties = {
  background: "#f3f4f6",
  borderRadius: "6px",
  padding: "20px",
  textAlign: "center",
  fontSize: "0.75rem",
  color: "#9ca3af",
  textTransform: "capitalize",
};

const checklistPreviewRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const checklistPill: React.CSSProperties = {
  fontSize: "0.7rem",
  padding: "2px 8px",
  border: "1px solid #e5e7eb",
  borderRadius: "4px",
  color: "#9ca3af",
};

export default FormPreview;
